import type { MouseEvent } from "react";
import { Sparkles } from "lucide-react";
import { useDayPlan } from "../../hooks/useDayPlan";
import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import type { RiskFlowPostSurface } from "./RiskFlowPostCard";

interface RiskFlowInfoBackCardProps {
  alert: RiskFlowAlert;
  surface: RiskFlowPostSurface;
  onRequestBreakdown?: () => void;
}

interface RelatedDeskEvent {
  id: string;
  label: string;
  time: string;
}

export function RiskFlowInfoBackCard({
  alert,
  surface,
  onRequestBreakdown,
}: RiskFlowInfoBackCardProps) {
  const { data: dayPlan } = useDayPlan();
  const narratives = relatedNarratives(alert);
  const relatedEvents = relatedDeskEvents(alert, dayPlan);

  return (
    <div
      data-riskflow-info-back-card="true"
      className={`relative flex w-full flex-col overflow-hidden rounded-[6px] border border-[var(--fintheon-accent)]/12 bg-[radial-gradient(circle_at_18%_0%,rgba(199,159,74,0.10),transparent_32%),var(--fintheon-bg)]/88 p-3.5 ${mediaHeight(surface)}`}
    >
      <section className="min-w-0">
        <h4 className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--fintheon-text)]/55">
          Related Narratives
        </h4>
        <p className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] leading-relaxed text-[var(--fintheon-text)]/72">
          {narratives.length > 0 ? (
            narratives.map((narrative, index) => (
              <span
                key={narrative.id}
                className="inline-flex items-center gap-1"
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: narrative.color }}
                  aria-hidden="true"
                />
                <span>{narrative.label}</span>
                {index < narratives.length - 1 ? (
                  <span className="text-[var(--fintheon-text)]/28">,</span>
                ) : null}
              </span>
            ))
          ) : (
            <span className="text-[var(--fintheon-text)]/38">
              Not linked yet
            </span>
          )}
        </p>
      </section>

      <section className="mt-4 min-w-0">
        <h4 className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--fintheon-text)]/55">
          Possibly Related To
        </h4>
        <div className="mt-2 space-y-1.5">
          {relatedEvents.length > 0 ? (
            relatedEvents.map((event) => (
              <div
                key={event.id}
                className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 text-[11px] leading-tight"
              >
                <span className="truncate text-[var(--fintheon-text)]/72">
                  {event.label}
                </span>
                <span className="font-mono text-[var(--fintheon-text)]/38 tabular-nums">
                  {event.time}
                </span>
              </div>
            ))
          ) : (
            <p className="text-[11px] text-[var(--fintheon-text)]/38">
              No scheduled desk-plan event matched.
            </p>
          )}
        </div>
      </section>

      <section className="mt-4 min-h-0 flex-1">
        <h4 className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--fintheon-text)]/55">
          Breakdown Summary
        </h4>
        <p className="mt-2 max-w-[38rem] text-[11px] leading-relaxed text-[var(--fintheon-text)]/42">
          Not requested yet.
        </p>
      </section>

      <button
        type="button"
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          onRequestBreakdown?.();
        }}
        className="solvys-liquid-glass fintheon-black-liquid-glass fintheon-black-liquid-glass--absolute riskflow-info-back-card__cta absolute right-3 bottom-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--fintheon-accent)] transition-[color,text-shadow,transform] hover:text-[var(--fintheon-text)] hover:[text-shadow:0_0_12px_color-mix(in_srgb,var(--fintheon-accent)_65%,transparent)] focus:outline-none focus-visible:text-[var(--fintheon-text)]"
      >
        <Sparkles className="h-3 w-3" />
        Ask AI
      </button>
    </div>
  );
}

function relatedNarratives(alert: RiskFlowAlert) {
  const raw = [
    ...(alert.narrativeThreads ?? []),
    alert.category ?? "",
    alert.riskType ?? "",
  ]
    .map((value) => value.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  return raw
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 6)
    .map((value) => ({
      id: value,
      label: titleize(value),
      color: narrativeColor(value),
    }));
}

function relatedDeskEvents(
  alert: RiskFlowAlert,
  dayPlan: ReturnType<typeof useDayPlan>["data"],
): RelatedDeskEvent[] {
  if (!dayPlan?.windows?.length) return [];
  const text = searchableAlertText(alert);
  const textTokens = tokenSet(text);
  return dayPlan.windows
    .filter((window) => {
      const label = window.eventName ?? dayPlan.eventName ?? dayPlan.deskTheme;
      if (!label) return false;
      const eventTokens = tokenSet(
        `${label} ${window.econForecast?.aiPrediction ?? ""} ${window.econForecast?.forecast ?? ""}`,
      );
      return overlapCount(textTokens, eventTokens) >= 2;
    })
    .slice(0, 3)
    .map((window) => ({
      id: window.id,
      label:
        window.eventName ??
        dayPlan.eventName ??
        dayPlan.deskTheme ??
        "Desk Plan Event",
      time: formatWindowTime(window.startTime),
    }));
}

function searchableAlertText(alert: RiskFlowAlert) {
  return [
    alert.headline,
    alert.summary,
    alert.category,
    alert.riskType,
    ...(alert.tags ?? []),
    ...(alert.narrativeThreads ?? []),
  ]
    .filter(Boolean)
    .join(" ");
}

function tokenSet(value: string): Set<string> {
  return new Set(
    value
      .toLowerCase()
      .replace(/\$[a-z0-9.]+/g, "")
      .split(/[^a-z0-9]+/)
      .map((token) => token.trim())
      .filter((token) => token.length > 2 && !STOP_WORDS.has(token)),
  );
}

function overlapCount(left: Set<string>, right: Set<string>) {
  let count = 0;
  for (const token of left) {
    if (right.has(token)) count += 1;
  }
  return count;
}

function titleize(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function narrativeColor(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 360;
  }
  return `hsl(${hash} 68% 58%)`;
}

function formatWindowTime(value: string) {
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return value;
  const hour = Number(match[1]);
  const minute = match[2];
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute}${suffix}`;
}

function mediaHeight(surface: RiskFlowPostSurface): string {
  return surface === "mini" ? "min-h-40" : "min-h-72";
}

const STOP_WORDS = new Set([
  "actual",
  "after",
  "and",
  "are",
  "before",
  "board",
  "but",
  "for",
  "forecast",
  "from",
  "has",
  "have",
  "index",
  "into",
  "not",
  "now",
  "previous",
  "report",
  "says",
  "the",
  "this",
  "with",
]);
